import './App.css';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './utils/PrivateRoute';
import PluginGuard from './components/PluginGuard';
import { AuthProvider } from './context/AuthContext';
import { PluginProvider } from './context/PluginContext';
import { SidebarBadgesProvider } from './context/SidebarBadgesContext';

// Light components — load eagerly (small, essential for first render)
import Login from './pages/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import Base from './components/Base';
import AdminRoute from './components/admin/AdminRoute';

// Medium components — lazy but preloaded
const LandingPage = lazy(() => import('./pages/landing_page'));
const HomePage = lazy(() => import('./pages/HomePage'));

// NOTE: We intentionally do NOT eagerly prefetch the TemplateThemes / VisualTemplateEditor
// chunks. TemplateThemes is a very large chunk; prefetching it for every visitor (even those
// who never open it) hurt initial load. These routes are React.lazy()-loaded on demand below,
// which is fast enough and avoids downloading a big chunk users may never need.

// Heavy components — load lazily (only when route is visited)
const WebsiteBuilderLanding = lazy(() => import('./pages/WebsiteBuilderLanding'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const TermsAndServices = lazy(() => import('./components/TermsNcondn'));
const ReturnPolicy = lazy(() => import('./components/ReturnPolicy'));
const PaymentShipping = lazy(() => import('./components/PaymentShipping'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Faqs = lazy(() => import('./components/Faqs'));
const Uploadproduct = lazy(() => import('./pages/Uploadproduct'));
const Profile = lazy(() => import('./components/Profile'));
const Contact = lazy(() => import('./components/Contact'));
const AboutMe = lazy(() => import('./components/AboutMe'));
const Orders = lazy(() => import('./components/Orders'));
const Proposals = lazy(() => import('./components/Proposals'));
const EditProduct = lazy(() => import('./components/EditProduct'));
const TemplateSelector = lazy(() => import('./components/Subscription'));
const TemplateThemes = lazy(() => import('./pages/TemplateThemes'));
// Public, no-login full-page render of a base template — used to auto-screenshot
// gallery cover images and for visual QA. Route: /template-shot/:templateId
const TemplateShot = lazy(() => import('./pages/TemplateShot'));
const VisualTemplateEditor = lazy(() => import('./pages/VisualTemplateEditor'));
const DesignerOnboarding = lazy(() => import('./components/DesignerOnboarding'));
const AdminDesignerDashboard = lazy(() => import('./components/AdminDesignerDashboard'));
const DesignerProfile = lazy(() => import('./components/DesignerProfile'));
const MyCoupons = lazy(() => import('./pages/MyCoupons'));
const Analytics = lazy(() => import('./pages/Analytics'));
const SEOSettings = lazy(() => import('./pages/SEOSettings'));
const CollectionsManager = lazy(() => import('./pages/CollectionsManager'));
const OrderBook = lazy(() => import('./components/orderbook/OrderBook'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const PageDashboard = lazy(() => import('./components/TemplateBuilder/PageDashboard'));
// L7: Version history browseable UI for template drafts
const VersionHistory = lazy(() => import('./pages/VersionHistory'));

// ─── Daily-Ops SaaS features (phases §1–§7) ────────────────────────────
// §1 Morning Dashboard
const Dashboard = lazy(() => import('./pages/Dashboard'));
// §4 Inventory
const InventoryIndex = lazy(() => import('./pages/Inventory'));
const VariantManager = lazy(() => import('./pages/Inventory/VariantManager'));
const StockHistory = lazy(() => import('./pages/Inventory/StockHistory'));
// §5 Customer CRM
const CustomersList = lazy(() => import('./pages/Customers'));
const CustomerDetail = lazy(() => import('./pages/Customers/CustomerDetail'));
const SegmentBuilder = lazy(() => import('./pages/Customers/SegmentBuilder'));
// §2 Unified Orders
const UnifiedInbox = lazy(() => import('./pages/Orders/UnifiedInbox'));
// §3 Production
const ProductionBoard = lazy(() => import('./pages/Production/ProductionBoard'));
const KarigarList = lazy(() => import('./pages/Production/KarigarList'));
const KarigarDetail = lazy(() => import('./pages/Production/KarigarDetail'));
// §6 Billing & Payments
const PaymentsDashboard = lazy(() => import('./pages/Payments/Dashboard'));
// Connect Payments — designer links their OWN Razorpay account (replaces KYC/Route onboarding)
const ConnectPayments = lazy(() => import('./pages/Payments/ConnectPayments'));
const ExpenseList = lazy(() => import('./pages/Expenses/ExpenseList'));
const SupplierLedger = lazy(() => import('./pages/Expenses/SupplierLedger'));
// §7 Reports
const ReportsPnL = lazy(() => import('./pages/Reports/PnL'));
const ReportsGSTR1 = lazy(() => import('./pages/Reports/GSTR1'));
const ReportsComparisons = lazy(() => import('./pages/Reports/Comparisons'));
const ReportsGoals = lazy(() => import('./pages/Reports/GoalTracker'));
// Automations / AI rules engine
const Automations = lazy(() => import('./pages/Automations'));
// WhatsApp — setup form + messages inbox, unified
const WhatsAppHub = lazy(() => import('./pages/WhatsApp'));
// Plugin Store — opt-in business features
const PluginStore = lazy(() => import('./pages/PluginStore'));
// Email Marketing — broadcasts + lifecycle emails
const EmailMarketing = lazy(() => import('./pages/EmailMarketing'));

// [LOW-9] Real 404 page. Previously the catch-all `*` redirected to
// /homepage (which, when unauthenticated, bounced to /login) — silently
// masking broken links and typo'd URLs. Render an explicit Not Found page
// instead so unknown routes are visible rather than disguised as a redirect.
function NotFound() {
  return (
    <div className="flex flex-col items-center justify-center h-screen text-center px-4">
      <h1 className="text-6xl font-extrabold text-[#d45614]">404</h1>
      <p className="mt-4 text-lg font-semibold text-gray-900">Page not found</p>
      <p className="mt-2 text-sm text-gray-500">
        The page you’re looking for doesn’t exist or may have moved.
      </p>
      <div className="mt-6 flex gap-3">
        <Link
          to="/homepage"
          className="px-4 py-2 rounded-lg text-white font-semibold"
          style={{ background: '#d45614' }}
        >
          Go to Dashboard
        </Link>
        <Link
          to="/"
          className="px-4 py-2 rounded-lg font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50"
        >
          Back Home
        </Link>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <Router>
      <AuthProvider>
        <PluginProvider>
        <SidebarBadgesProvider>
        <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div></div>}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/old-landing" element={<LandingPage />} />
          <Route path="/contactus" element={<ContactUs />} />
          <Route path="/login" element={<Login />} />
          <Route path="/termsNconditions" element={<TermsAndServices/>} />
          <Route path="/payShip" element={<PaymentShipping/>} />
          <Route path="/rtnPolicy" element={<ReturnPolicy/>} />
          <Route path="/privacyPolicy" element={<PrivacyPolicy/>} />
          <Route path="/Faqs" element={<Faqs/>} />
          <Route path="/forgot-password" element={<ForgotPassword/>} />
          <Route path="/template-shot/:templateId" element={<TemplateShot />} />
          <Route
            path="/homepage"
            element={
              <PrivateRoute>
                <HomePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/UploadProduct"
            element={
              <PrivateRoute>
                <Uploadproduct/>
              </PrivateRoute>
            }
          />
          <Route
            path="/EditProduct/:productId"
            element={
              <PrivateRoute>
                <EditProduct/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Base"
            element={
              <PrivateRoute>
                <Base/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Profile"
            element={
              <PrivateRoute>
                <Profile/>
              </PrivateRoute>
            }
          />
          <Route
            path="/my-coupons"
            element={
              <PrivateRoute>
                <MyCoupons/>
              </PrivateRoute>
            }
          />
          <Route
            path="/collections-manager"
            element={
              <PrivateRoute>
                <CollectionsManager/>
              </PrivateRoute>
            }
          />
          <Route
            path="/TemplateThemes"
            element={
              <PrivateRoute>
                <TemplateThemes/>
              </PrivateRoute>
            }
          />
          <Route
            path="/visual-editor"
            element={
              <PrivateRoute>
                <VisualTemplateEditor/>
              </PrivateRoute>
            }
          />
          <Route
            path="/template/:templateId/versions"
            element={
              <PrivateRoute>
                <VersionHistory/>
              </PrivateRoute>
            }
          />
          <Route
            path="/template-builder/:templateId"
            element={
              <PrivateRoute>
                <PageDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/Contact"
            element={
              <PrivateRoute>
                <Contact/>
              </PrivateRoute>
            }
          />
          <Route
            path="/AboutMe"
            element={
              <PrivateRoute>
                <AboutMe/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Orders"
            element={
              <PrivateRoute>
                <Orders/>
              </PrivateRoute>
            }
          />
          <Route
            path="/orderbook"
            element={
              <PrivateRoute>
                <OrderBook/>
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Analytics/>
              </PrivateRoute>
            }
          />
          <Route
            path="/seo-settings"
            element={
              <PrivateRoute>
                <SEOSettings/>
              </PrivateRoute>
            }
          />
          <Route
            path="/Proposals"
            element={
              <PrivateRoute>
                <PluginGuard pluginKey="bid_requests">
                  <Proposals/>
                </PluginGuard>
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <PrivateRoute>
                <TemplateSelector />
              </PrivateRoute>
            }
          />
          <Route
            path="/vasstr-admin"
            element={
              <AdminRoute>
                <AdminDashboard/>
              </AdminRoute>
            }
          />
          <Route
            path="/designer-onboarding"
            element={
              <PrivateRoute>
                <DesignerOnboarding/>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin-designers"
            element={
              <AdminRoute>
                <AdminDesignerDashboard/>
              </AdminRoute>
            }
          />
          <Route
            path="/designer-profile"
            element={
              <PrivateRoute>
                <DesignerProfile/>
              </PrivateRoute>
            }
          />
          <Route path="/Register" element={<Register/>}/>

          {/* ─── Plugin-gated feature routes — one plugin per sidebar item ─── */}
          <Route path="/dashboard" element={<PrivateRoute><PluginGuard pluginKey="daily_ops"><Dashboard/></PluginGuard></PrivateRoute>} />
          <Route path="/inventory" element={<PrivateRoute><PluginGuard pluginKey="inventory"><InventoryIndex/></PluginGuard></PrivateRoute>} />
          <Route path="/inventory/product/:productId" element={<PrivateRoute><PluginGuard pluginKey="inventory"><VariantManager/></PluginGuard></PrivateRoute>} />
          <Route path="/inventory/variants/:variantId/history" element={<PrivateRoute><PluginGuard pluginKey="inventory"><StockHistory/></PluginGuard></PrivateRoute>} />
          <Route path="/customers" element={<PrivateRoute><PluginGuard pluginKey="customers"><CustomersList/></PluginGuard></PrivateRoute>} />
          <Route path="/customers/:id" element={<PrivateRoute><PluginGuard pluginKey="customers"><CustomerDetail/></PluginGuard></PrivateRoute>} />
          <Route path="/customer-segments" element={<PrivateRoute><PluginGuard pluginKey="customers"><SegmentBuilder/></PluginGuard></PrivateRoute>} />
          <Route path="/orders-unified" element={<PrivateRoute><PluginGuard pluginKey="unified_inbox"><UnifiedInbox/></PluginGuard></PrivateRoute>} />
          <Route path="/production" element={<PrivateRoute><PluginGuard pluginKey="production_board"><ProductionBoard/></PluginGuard></PrivateRoute>} />
          <Route path="/karigars" element={<PrivateRoute><PluginGuard pluginKey="team"><KarigarList/></PluginGuard></PrivateRoute>} />
          <Route path="/karigars/:id" element={<PrivateRoute><PluginGuard pluginKey="team"><KarigarDetail/></PluginGuard></PrivateRoute>} />
          <Route path="/payments" element={<PrivateRoute><PluginGuard pluginKey="payments"><PaymentsDashboard/></PluginGuard></PrivateRoute>} />
          <Route path="/connect-payments" element={<PrivateRoute><ConnectPayments/></PrivateRoute>} />
          <Route path="/expenses" element={<PrivateRoute><PluginGuard pluginKey="expenses"><ExpenseList/></PluginGuard></PrivateRoute>} />
          <Route path="/suppliers" element={<PrivateRoute><PluginGuard pluginKey="suppliers"><SupplierLedger/></PluginGuard></PrivateRoute>} />
          <Route path="/suppliers/:id" element={<PrivateRoute><PluginGuard pluginKey="suppliers"><SupplierLedger/></PluginGuard></PrivateRoute>} />
          <Route path="/reports/pnl" element={<PrivateRoute><PluginGuard pluginKey="report_pnl"><ReportsPnL/></PluginGuard></PrivateRoute>} />
          <Route path="/reports/gstr1" element={<PrivateRoute><PluginGuard pluginKey="report_gstr1"><ReportsGSTR1/></PluginGuard></PrivateRoute>} />
          <Route path="/reports/comparisons" element={<PrivateRoute><ReportsComparisons/></PrivateRoute>} />
          <Route path="/reports/goals" element={<PrivateRoute><PluginGuard pluginKey="report_goals"><ReportsGoals/></PluginGuard></PrivateRoute>} />
          <Route path="/whatsapp" element={<PrivateRoute><PluginGuard pluginKey="whatsapp"><WhatsAppHub/></PluginGuard></PrivateRoute>} />
          <Route path="/automations" element={<PrivateRoute><PluginGuard pluginKey="automations"><Automations/></PluginGuard></PrivateRoute>} />
          <Route path="/email-marketing" element={<PrivateRoute><PluginGuard pluginKey="email_marketing"><EmailMarketing/></PluginGuard></PrivateRoute>} />

          {/* Plugin Store — always available */}
          <Route path="/plugins" element={<PrivateRoute><PluginStore/></PrivateRoute>} />

          {/* [LOW-9] Real 404 instead of a silent redirect to /homepage. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </SidebarBadgesProvider>
        </PluginProvider>
      </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;